/**
 * P80 Custom Collection / Search Ordering
 *
 * Applies CSS custom property-based ordering to product grid items so that
 * merchant-defined "custom blocks" (`.product-grid__item-custom`) land at
 * specific desktop / mobile positions inside the grid.
 *
 * Custom blocks carry inline `--desktop-position` and `--mobile-position`
 * set server-side (from the metaobject entry / theme setting). This module
 * reads those positions, then shifts every regular `.product-grid__item`
 * (by its `product-grid__item--<n>` index) so that the CSS `order` property
 * places the custom blocks at their intended slots without reordering the
 * product cards' DOM source.
 *
 * It auto-runs on initial load and re-runs whenever the grid's children
 * change (infinite-scroll appends and Section Rendering API morphs) via a
 * MutationObserver per `.p80-collection` container.
 *
 * @module p80-custom-ordering
 */

/**
 * Apply custom ordering CSS custom properties to a product grid inside the
 * given container. Reads `--desktop-position` / `--mobile-position` from
 * `.product-grid__item-custom` items, then computes shifted positions for
 * regular `.product-grid__item` items.
 *
 * Safe to call repeatedly; does nothing if no custom items are present.
 *
 * @param {Element} container - The container element that holds the grid
 *                              (e.g. the section wrapper or `.p80-collection`).
 */
export function applyCustomOrdering(container) {
    const grid = container.querySelector('.product-grid');
    if (!grid) return;

    const customItems = grid.querySelectorAll('.product-grid__item-custom');
    if (!customItems.length) return;

    const desktopPositions = [];
    const mobilePositions = [];

    customItems.forEach(function (item) {
        const style = item.getAttribute('style') || '';
        const dMatch = style.match(/--desktop-position:\s*(\d+)/);
        const mMatch = style.match(/--mobile-position:\s*(\d+)/);
        if (dMatch) desktopPositions.push(parseInt(dMatch[1], 10));
        if (mMatch) mobilePositions.push(parseInt(mMatch[1], 10));
    });

    if (!desktopPositions.length && !mobilePositions.length) return;

    desktopPositions.sort(function (a, b) {
        return a - b;
    });
    mobilePositions.sort(function (a, b) {
        return a - b;
    });

    const regularItems = grid.querySelectorAll(
        '.product-grid__item:not(.product-grid__item-custom)'
    );

    regularItems.forEach(function (item) {
        if (!(item instanceof HTMLElement)) return;
        const match = item.className.match(/product-grid__item--(\d+)/);
        if (!match) return;
        const n = parseInt(match[1], 10);

        let dShift = 0;
        for (let i = 0; i < desktopPositions.length; i++) {
            if (desktopPositions[i] <= n + dShift) {
                dShift++;
            } else {
                break;
            }
        }

        let mShift = 0;
        for (let j = 0; j < mobilePositions.length; j++) {
            if (mobilePositions[j] <= n + mShift) {
                mShift++;
            } else {
                break;
            }
        }

        item.style.setProperty('--desktop-position', String(n + dShift));
        item.style.setProperty('--mobile-position', String(n + mShift));
    });
}

/**
 * Wire a MutationObserver to a container so ordering re-applies whenever the
 * grid's children change (infinite-scroll appends, Section Rendering API morph).
 *
 * @param {Element} container - The `.p80-collection` container to observe.
 */
function observeContainer(container) {
    const grid = container.querySelector('.product-grid');
    if (!grid) return;

    applyCustomOrdering(container);

    let pending = false;
    const observer = new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
            pending = false;
            applyCustomOrdering(container);
        });
    });

    observer.observe(grid, { childList: true, subtree: false });

    // Keep the observer reachable so it isn't garbage-collected.
    container.__p80OrderingObserver = observer;
}

/**
 * Initialize ordering for every `.p80-collection` container on the page.
 */
function initAll() {
    const containers = document.querySelectorAll('.p80-collection:not([data-p80-ordering-init])');
    containers.forEach(function (container) {
        container.setAttribute('data-p80-ordering-init', '');
        observeContainer(container);
    });
}

// Auto-run on initial page load when this module is loaded via a <script> tag.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
} else {
    initAll();
}

// Re-scan after Section Rendering API morphs (a section may be swapped in late).
document.addEventListener('shopify:section:load', initAll);
